import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { CUSTOMER_TYPES, COUNTRY_OPTIONS } from '../../constants/profileOptions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// InputField défini EN DEHORS du composant pour éviter les re-renders qui font perdre le focus
const InputField = ({ id, label, value, onChange, required = false, placeholder, t }) => (
  <div className="w-full">
    <label htmlFor={id} className="block text-xs sm:text-sm font-medium text-slate-300 mb-1">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    <input
      id={id}
      name={id}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      autoComplete="off"
      className="w-full px-3 py-2.5 sm:py-2 rounded-lg border border-slate-600 bg-slate-800 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
    />
  </div>
);

const BillingInfoModal = () => {
  const { billingCheck, updateUserProfile, userProfile, closeBillingModal, refreshUserProfile } = useAuth();
  const { t } = useTranslation('billing');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    vatNumber: '',
    siret: '',
    phone: '',
    customerType: 'individual',
    addressLine1: '',
    addressLine2: '',
    postalCode: '',
    city: '',
    country: 'FR',
  });

  useEffect(() => {
    if (userProfile?.billing) {
      setFormData(prev => ({
        ...prev,
        firstName: userProfile.billing.first_name || '',
        lastName: userProfile.billing.last_name || '',
        companyName: userProfile.billing.company_name || '',
        vatNumber: userProfile.billing.vat_number || '',
        siret: userProfile.billing.siret || '',
        phone: userProfile.billing.phone || '',
        customerType: userProfile.billing.customer_type || 'individual',
        addressLine1: userProfile.billing.address?.line1 || '',
        addressLine2: userProfile.billing.address?.line2 || '',
        postalCode: userProfile.billing.address?.postal_code || '',
        city: userProfile.billing.address?.city || '',
        country: userProfile.billing.address?.country || 'FR',
      }));
    }
  }, [userProfile]);

  // Only show modal when explicitly requested for a purchase
  if (!billingCheck?.showModal) return null;
  
  const handleCancel = () => {
    closeBillingModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Basic validation
    if (!formData.firstName || !formData.lastName || !formData.addressLine1 || !formData.postalCode || !formData.city) {
      setError(t('modal.errorRequired'));
      setLoading(false);
      return;
    }

    try {
      await updateUserProfile({
        billing: {
          first_name: formData.firstName,
          last_name: formData.lastName,
          company_name: formData.companyName,
          vat_number: formData.vatNumber,
          siret: formData.siret,
          phone: formData.phone,
          customer_type: formData.customerType,
          address: {
            line1: formData.addressLine1,
            line2: formData.addressLine2,
            postal_code: formData.postalCode,
            city: formData.city,
            country: formData.country,
          },
        },
      });
      await refreshUserProfile();
      closeBillingModal(true);
    } catch (err) {
      console.error("Failed to update billing info", err);
      setError(t('modal.errorSaving'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9998] flex items-start sm:items-center justify-center bg-black/90 backdrop-blur-md p-2 sm:p-4 overflow-y-auto">
      <div className="bg-slate-900 border border-slate-700 rounded-xl sm:rounded-2xl max-w-2xl w-full shadow-2xl flex flex-col my-2 sm:my-8 animate-in fade-in zoom-in duration-300 max-h-[calc(100vh-1rem)] sm:max-h-[calc(100vh-4rem)]">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-slate-800 bg-slate-900/50 flex-shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg sm:text-2xl font-bold text-white">{t('modal.title')}</h2>
              <p className="text-slate-400 text-xs sm:text-sm mt-1 sm:mt-2">
                {t('modal.subtitle')}
              </p>
            </div>
            <button
              type="button"
              onClick={handleCancel}
              className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-all"
            >
              <FontAwesomeIcon icon="times" className="text-sm sm:text-base" />
            </button>
          </div>
        </div>
        
        {/* Scrollable Form Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6">
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-xs sm:text-sm">
              {error}
            </div>
          )}

          {/* Name Fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <InputField
              id="firstName"
              label={t('modal.firstName')}
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              required
            />
            <InputField
              id="lastName"
              label={t('modal.lastName')}
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              required
            />
          </div>

          {/* Account Type */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-slate-300 mb-2">{t('modal.accountType')}</label>
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              {CUSTOMER_TYPES.map((option) => (
                <button
                  type="button"
                  key={option.value}
                  onClick={() => setFormData({ ...formData, customerType: option.value })}
                  className={`py-2.5 sm:py-2 px-3 sm:px-4 rounded-lg text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                    formData.customerType === option.value
                      ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/20'
                      : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                  }`}
                >
                  <FontAwesomeIcon 
                    icon={option.value === 'business' ? 'building' : 'user'} 
                    className="text-xs" 
                  />
                  <span className="truncate">{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Business Fields */}
          {formData.customerType === 'business' && (
            <div className="space-y-3 sm:space-y-4 p-3 sm:p-4 bg-slate-800/50 rounded-xl border border-slate-700/50 animate-in fade-in zoom-in-95 duration-300">
              <InputField
                id="companyName"
                label={t('modal.companyName')}
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                required
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <InputField
                  id="vatNumber"
                  label={t('modal.vatNumber')}
                  value={formData.vatNumber}
                  onChange={(e) => setFormData({ ...formData, vatNumber: e.target.value })}
                  placeholder="FRXX999999999"
                />
                <InputField
                  id="siret"
                  label={t('modal.siret')}
                  value={formData.siret}
                  onChange={(e) => setFormData({ ...formData, siret: e.target.value })}
                />
              </div>
            </div>
          )}

          {/* Address Fields */}
          <div className="space-y-3 sm:space-y-4">
            <InputField
              id="addressLine1"
              label={t('modal.address')}
              value={formData.addressLine1}
              onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
              required
            />
            <InputField
              id="addressLine2"
              label={t('modal.addressComplement')}
              value={formData.addressLine2}
              onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
            />
            
            {/* Postal Code and City */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
              <InputField
                id="postalCode"
                label={t('modal.postalCode')}
                value={formData.postalCode}
                onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                required
              />
              <div className="col-span-1 sm:col-span-2">
                <InputField
                  id="city"
                  label={t('modal.city')}
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  required
                />
              </div>
            </div>
            
            {/* Country */}
            <div className="w-full">
              <label htmlFor="country" className="block text-xs sm:text-sm font-medium text-slate-300 mb-1">
                {t('modal.country')} <span className="text-red-400">*</span>
              </label>
              <select
                id="country"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                className="w-full px-3 py-2.5 sm:py-2 rounded-lg border border-slate-600 bg-slate-800 text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              >
                {COUNTRY_OPTIONS.map((opt) => (
                  <option key={opt.code} value={opt.code}>{opt.label}</option>
                ))}
              </select>
            </div>
            
            {/* Phone */}
            <InputField
              id="phone"
              label={t('modal.phone')}
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+33..."
            />
          </div>
        </form>

        {/* Fixed Footer */}
        <div className="p-4 sm:p-6 border-t border-slate-800 flex-shrink-0 bg-slate-900/50">
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 sm:justify-end">
            <button
              type="button"
              onClick={handleCancel}
              className="w-full sm:w-auto px-6 py-3 sm:py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-sm font-medium transition-all"
            >
              {t('modal.cancel')}
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={loading}
              className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-semibold text-sm transition-all shadow-lg shadow-indigo-500/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon="circle-notch" spin className="text-sm" />
                  <span>{t('modal.saving')}</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon="save" className="text-sm" />
                  <span>{t('modal.save')}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingInfoModal;
